import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Factory, Package, Truck, ThermometerSnowflake, Users, CheckCircle2, Shield } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { facilities } from '../mock';

const Infrastructure = () => {
  const iconMap = {
    Factory,
    Package,
    Truck,
    ThermometerSnowflake,
    Users,
    Shield
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <Helmet>
        <title>Infrastructure & Partner Facilities | Processing, Cold Storage – Agro Corp India</title>
        <meta name="description" content="Agro Corp India's infrastructure: certified partner processing units, quality control labs, cold storage, and logistics. HACCP & ISO-compliant facilities for export-quality products." />
        <meta name="keywords" content="agricultural infrastructure India, food processing facility, cold storage export, quality control lab, HACCP compliant, export infrastructure Pune" />
        <link rel="canonical" href="https://www.agrocorpindia.com/infrastructure" />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative h-[50vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://images.unsplash.com/photo-1589313331637-fa8ceb1fac0d')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our Infrastructure
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            World-class facilities ensuring quality at every step
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider bg-amber-50 px-4 py-2 rounded-full inline-block mb-6">
            Infrastructure & Facilities
          </span>
          <h2 className="text-4xl font-bold text-green-900 mb-6">
            Partner Facilities Network
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            Agro Corp India operates through a network of certified partner facilities that meet hygiene, safety, and quality standards. These facilities support our storage, processing, and packaging requirements until we establish our own warehouse in the near future.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mt-4">
            We work with certified partner storage, processing, and packaging facilities in Pune and surrounding regions to maintain quality and compliance with international standards.
          </p>
        </div>
      </section>

      {/* Facilities Grid */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {facilities.map((facility) => {
              const IconComponent = iconMap[facility.icon];
              return (
                <Card
                  key={facility.id}
                  className="hover:shadow-2xl transition-all duration-300 border-2 hover:border-green-200 group"
                >
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-8 h-8 text-green-700" />
                    </div>
                    <h3 className="text-2xl font-bold text-green-900 mb-3">{facility.title}</h3>
                    <p className="text-gray-600 mb-6">{facility.description}</p>
                    <div className="space-y-2">
                      {facility.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Infrastructure Images */}
      <section className="py-20 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-green-900 mb-4">
              A Glimpse of Our Facilities
            </h2>
            <p className="text-lg text-gray-600">
              Modern infrastructure supporting quality production
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl group">
              <img
                src="https://images.unsplash.com/photo-1589313331637-fa8ceb1fac0d"
                alt="Processing facility"
                className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Processing Unit</h3>
                  <p className="text-green-100">Modern equipment and controlled environment</p>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl shadow-2xl group">
              <img
                src="https://images.pexels.com/photos/5620667/pexels-photo-5620667.jpeg"
                alt="Warehouse facility"
                className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Storage & Warehousing</h3>
                  <p className="text-green-100">Temperature-controlled storage facilities</p>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl shadow-2xl group">
              <img
                src="https://images.unsplash.com/photo-1566735049879-444f282d77f9"
                alt="Agricultural fields"
                className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Source Farms</h3>
                  <p className="text-green-100">Direct sourcing from certified farms</p>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl shadow-2xl group">
              <img
                src="https://images.unsplash.com/photo-1618515299844-7b810f9301df"
                alt="Quality control"
                className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Quality Testing</h3>
                  <p className="text-green-100">Advanced laboratory equipment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology & Innovation */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-green-200">
            <CardContent className="p-12">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Factory className="w-8 h-8 text-amber-700" />
                </div>
                <h2 className="text-3xl font-bold text-green-900 mb-4">
                  Quality Through Partnership
                </h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <p className="leading-relaxed">
                  We work exclusively with certified partner facilities that meet international quality and safety standards. 
                  These partnerships allow us to ensure consistent quality while we work towards establishing our own 
                  infrastructure in the future.
                </p>
                <p className="leading-relaxed">
                  Each partner facility undergoes rigorous evaluation and regular audits to maintain compliance with 
                  FSSAI, APEDA, and international export standards. We maintain complete traceability through digital 
                  systems that track each product through the supply chain.
                </p>
                <div className="grid grid-cols-2 gap-4 pt-6">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-green-700">2025</p>
                    <p className="text-sm text-gray-600">Year Established</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-green-700">100%</p>
                    <p className="text-sm text-gray-600">Quality Commitment</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-green-700 to-green-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Visit Our Facilities
          </h2>
          <p className="text-xl text-green-100 mb-8">
            We welcome visits from our partners and clients to experience our infrastructure firsthand
          </p>
          <p className="text-green-100">
            Contact us to schedule a facility tour
          </p>
        </div>
      </section>
    </div>
  );
};

export default Infrastructure;
